import { CSSProperties, Fragment, PropsWithChildren } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";
import { parseCasePath } from "../../features/caseIde/routes";
import { useHistoryNav } from "../../features/navigation/useHistoryNav";
import {
  IndividualProvider,
  useIndividualOptional
} from "../../contexts/IndividualContext";
import CustomBackButton from "./CustomBackButton";

type SpaceValue = number | string;

/**
 * The Chakra style props existing callers still pass. Numbers are Chakra
 * spacing units (0.25rem); strings are raw CSS values.
 */
type LegacyStyleProps = {
  my?: SpaceValue;
  px?: SpaceValue;
  py?: SpaceValue;
  p?: SpaceValue;
  padding?: SpaceValue;
  flex?: string | number;
  minH?: SpaceValue;
};

type Props = {
  withBackNav?: boolean;
  backTargetPage?: string;
  flushContent?: boolean;
  bg?: string;
  onClick?: () => void;
  className?: string;
  breadcrumbs?: Array<{
    label: React.ReactNode;
    to?: string;
    onBreadcrumbClick?: () => void;
    active?: boolean;
  }>;
} & PropsWithChildren &
  LegacyStyleProps;

const toCss = (value: SpaceValue | undefined): string | undefined => {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${value * 0.25}rem` : value;
};

const legacyStyle = ({
  my,
  px,
  py,
  p,
  padding,
  flex,
  minH
}: LegacyStyleProps): CSSProperties => {
  const pad = toCss(padding ?? p);
  const style: CSSProperties = {};
  if (pad !== undefined) style.padding = pad;
  if (my !== undefined) {
    style.marginTop = toCss(my);
    style.marginBottom = toCss(my);
  }
  if (px !== undefined) {
    style.paddingLeft = toCss(px);
    style.paddingRight = toCss(px);
  }
  if (py !== undefined) {
    style.paddingTop = toCss(py);
    style.paddingBottom = toCss(py);
  }
  if (flex !== undefined) style.flex = flex;
  if (minH !== undefined) style.minHeight = toCss(minH);
  return style;
};

export const SectionWrapper = ({
  withBackNav = true,
  flushContent = false,
  children,
  backTargetPage = "Dashboard",
  onClick = undefined,
  breadcrumbs,
  bg,
  className,
  ...legacy
}: Props) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { id, caseID } = params as Record<string, string | undefined>;
  const individualId = id ?? caseID;
  // Under a case route (`/clients/:fn/cases/:id/*` or `/cases/:id/*`).
  const isCaseRoute = parseCasePath(location.pathname) !== null;
  const individualContext = useIndividualOptional();
  const backAriaLabel = `Back to ${backTargetPage}`;

  const isIndividualCaseShell = !withBackNav && isCaseRoute;
  const noInlinePadding = flushContent || isIndividualCaseShell;
  const restStyle = legacyStyle(legacy);

  // Back goes through in-app history when there is any; otherwise up to the
  // last navigable crumb, else home.
  const parentTo =
    breadcrumbs
      ?.slice()
      .reverse()
      .find((c) => typeof c.to === "string" && c.to.length > 0)?.to ?? "/";
  const { goBack } = useHistoryNav({ fallbackBackTo: parentTo });

  const handleBreadcrumbBack = () => {
    if (onClick) {
      onClick();
      return;
    }
    goBack();
  };

  const breadcrumbRow = (() => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    const row = (
      <div className="mb-4 flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Back"
          onClick={handleBreadcrumbBack}
        >
          <ArrowLeft />
        </Button>
        <Breadcrumb aria-label="Breadcrumb">
          <BreadcrumbList className="text-base text-fg-3">
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              const isActive = Boolean(crumb.active) || isLast;
              const onItemClick = () => {
                if (isActive) return;
                if (crumb.onBreadcrumbClick) {
                  crumb.onBreadcrumbClick();
                  return;
                }
                if (crumb.to) navigate(crumb.to);
              };

              return (
                <Fragment key={`${idx}`}>
                  <BreadcrumbItem>
                    {isActive ? (
                      <BreadcrumbPage className="line-clamp-1 text-fg">
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <button
                          type="button"
                          className="line-clamp-1 cursor-pointer text-fg-3 hover:text-fg"
                          onClick={onItemClick}
                        >
                          {crumb.label}
                        </button>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {!isLast && <BreadcrumbSeparator className="text-fg-3" />}
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    );

    // If breadcrumb labels rely on IndividualContext, ensure it exists.
    if (individualId && !individualContext) {
      return (
        <IndividualProvider individualId={individualId}>
          {row}
        </IndividualProvider>
      );
    }
    return row;
  })();

  return (
    <div
      className={cn(
        "mx-auto my-6 w-full max-w-full overflow-visible",
        noInlinePadding ? "px-0" : "px-4",
        isIndividualCaseShell && "flex min-h-0 flex-col",
        className
      )}
      style={{
        ...restStyle,
        ...(noInlinePadding ? { paddingLeft: 0, paddingRight: 0 } : {})
      }}
    >
      <div className={cn(isIndividualCaseShell && "flex-none")}>
        <div className="flex flex-col items-start justify-start gap-4">
          {withBackNav &&
            (breadcrumbRow ?? (
              <CustomBackButton ariaLabel={backAriaLabel} onClick={onClick} />
            ))}
        </div>
      </div>

      <div
        className={cn(
          "mx-auto w-full max-w-full",
          !bg && "bg-chrome",
          noInlinePadding ? "px-0 py-0" : "px-4 py-4",
          isIndividualCaseShell
            ? "min-h-0 flex-1 overflow-hidden rounded-tl-none"
            : "overflow-visible"
        )}
        style={{
          ...restStyle,
          ...(bg ? { background: bg } : {}),
          ...(noInlinePadding
            ? {
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0,
                paddingBottom: 0
              }
            : { paddingTop: "1rem", paddingBottom: "1rem" })
        }}
      >
        {children}
      </div>
    </div>
  );
};
